import {
  type BasicCheckMerchantsResponse,
  NowSlotExtendedTypeEnum,
  type NowSlotExtendedV2,
} from '@redteclab/api/clients/bully'

import { NOW_NOT_ELIGIBLE_REASON_MINIMUM_VALUE_NOT_REACHED } from './NOW_NOT_ELIGIBLE_REASON'

/**
 * used if to show now details for check on specific product.
 * it may be that the product is not eligible due to no minimum value reached.
 * in this case we anyway want to show the now details for the product.
 */
export const nowBasicCheckRespIsEligibleOrMinimumValueNotReached = (
  response?: BasicCheckMerchantsResponse,
): boolean => {
  if (!response) {
    return false
  }

  if (response.nowEligible) {
    return true
  }

  return (
    response.notEligibleReason ===
    NOW_NOT_ELIGIBLE_REASON_MINIMUM_VALUE_NOT_REACHED
  )
}

/**
 * slot.orderEndDateTime is given in YYYY-MM-DD HH:mm:ss format which can't be easily parsed.
 * function accepts string in format "YYYY-MM-DD HH:mm:ss" transforms to "YYYY-MM-DDTHH:mm:ss.000000+01:00"
 * and returns Date object.
 */
export const nowBasicCheckRespGetSlotOrderEndDateTimeInIsoBerlinTime = (
  slot: NowSlotExtendedV2,
): Date | undefined => {
  const endTimeString = slot.orderEndDateTime
  if (!endTimeString) {
    return undefined
  }

  // eslint-disable-next-line unicorn/try-complexity -- don't reference, refactor if touched
  try {
    /* eslint-disable unicorn/prefer-split-limit -- don't reference, refactor if touched */
    const [datePart, timePart] = endTimeString.split(' ')
    const [year, month, day] = datePart.split('-')
    const [hours, minutes, seconds] = timePart.split(':')
    /* eslint-enable unicorn/prefer-split-limit */

    if (!year || !month || !day || !hours || !minutes || !seconds) {
      return undefined
    }

    const date = new Date(
      `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.000000`,
    )
    // check if valid date
    if (Number.isNaN(date.getTime())) {
      return undefined
    }

    return date
  } catch {
    return undefined
  }
}

export const nowBasicCheckRespGetSlotShippingIsToday = (
  slot: NowSlotExtendedV2,
): boolean => {
  return slot.type === NowSlotExtendedTypeEnum.SameDay
}

const nowBasicCheckRespGetSlotsGroupedByType = (
  response: BasicCheckMerchantsResponse,
): { nextDaySlots: NowSlotExtendedV2[]; sameDaySlots: NowSlotExtendedV2[] } => {
  const { slots } = response
  const sameDaySlots: NowSlotExtendedV2[] = []
  const nextDaySlots: NowSlotExtendedV2[] = []

  if (slots) {
    for (const slot of slots) {
      if (slot.type === NowSlotExtendedTypeEnum.SameDay) {
        sameDaySlots.push(slot)
      }
      if (slot.type === NowSlotExtendedTypeEnum.NextDay) {
        nextDaySlots.push(slot)
      }
    }
  }

  return { nextDaySlots, sameDaySlots }
}

/** used for sorting **/
const compareSlotsBySecondsLeft = (
  aSlot: NowSlotExtendedV2,
  bSlot: NowSlotExtendedV2,
): number => {
  return (aSlot.secondsLeft ?? 0) - (bSlot.secondsLeft ?? 0)
}

/**
 * returns slots grouped by type and sorted by seconds left.
 */
const nowBasicCheckRespGetSlotGroupsSortedBySecondsLeft = (
  response: BasicCheckMerchantsResponse,
): { nextDaySlots: NowSlotExtendedV2[]; sameDaySlots: NowSlotExtendedV2[] } => {
  const { nextDaySlots, sameDaySlots } =
    nowBasicCheckRespGetSlotsGroupedByType(response)

  sameDaySlots.sort(compareSlotsBySecondsLeft)
  nextDaySlots.sort(compareSlotsBySecondsLeft)

  return { nextDaySlots, sameDaySlots }
}

/**
 * returns the slot which will be used for "time left countdown" https://jira.shop-apotheke.com/browse/WSTRA-5351
 */
export const nowBasicCheckRespGetSlotForCountdown = (
  response: BasicCheckMerchantsResponse,
): NowSlotExtendedV2 | undefined => {
  const { nextDaySlots, sameDaySlots } =
    nowBasicCheckRespGetSlotGroupsSortedBySecondsLeft(response)

  if (sameDaySlots.length > 0) {
    return sameDaySlots.at(-1)
  }

  if (nextDaySlots.length > 0) {
    return nextDaySlots.at(-1)
  }

  return undefined
}

export const nowBasicCheckGetSlotsByType = (
  response: BasicCheckMerchantsResponse,
  type: NowSlotExtendedTypeEnum,
): NowSlotExtendedV2[] => {
  const { nextDaySlots, sameDaySlots } =
    nowBasicCheckRespGetSlotsGroupedByType(response)

  if (type === NowSlotExtendedTypeEnum.SameDay) {
    return sameDaySlots
  }

  return nextDaySlots
}

export const nowBasicCheckRespHasNowAvailabilityInSelectedArea = (
  response: BasicCheckMerchantsResponse | undefined,
): boolean =>
  Boolean(response?.zipCodeInDeliveryRegion) ||
  Boolean(response?.zipCodeInPickupRegion)
